import {
  associationRepo,
  teamRepo,
  userRepo,
} from "@repositories";
import { WEME_ORGANIZATION_ID } from "@shared/helpers/constants";
import { OrganizationRoles, Roles } from "@shared/Roles";

export function formatPhoneForPartnerManager(raw: string) {
  const digits = raw.replace(/\D/g, "");
  if (digits.startsWith("972")) return `+${digits}`;
  if (digits.startsWith("0")) return `+972${digits.slice(1)}`;
  if (digits.length === 9) return `+972${digits}`;
  return raw.startsWith("+") ? raw : `+${digits}`;
}

export async function ensurePartnerManagerAssociation(userId: string) {
  const existing = await associationRepo.findOne({
    where: { userId, organizationId: WEME_ORGANIZATION_ID },
  });

  if (existing) {
    const roles = existing.roles.filter(
      (r) => r !== OrganizationRoles.Volunteer,
    );
    if (!roles.includes(OrganizationRoles.Organization)) {
      roles.push(OrganizationRoles.Organization);
    }
    if (!roles.includes(OrganizationRoles.Requesting)) {
      roles.push(OrganizationRoles.Requesting);
    }
    const unchanged =
      roles.length === existing.roles.length &&
      roles.every((r, i) => r === existing.roles[i]);
    if (!unchanged) {
      await associationRepo.update(existing.id, { roles });
    }
    return;
  }

  const association = await associationRepo.insert({
    userId,
    organizationId: WEME_ORGANIZATION_ID,
    roles: [OrganizationRoles.Organization, OrganizationRoles.Requesting],
    activityStatus: "NEW",
  });

  const mainTeam = await teamRepo.findOne({
    where: {
      organizationId: WEME_ORGANIZATION_ID,
      isMainOrganizationTeam: true,
    },
  });
  if (mainTeam) {
    await associationRepo.relations(association).teams.insert({
      teamId: mainTeam.id,
    });
  }
}

export async function removePartnerManagerAssociationRole(userId: string) {
  const association = await associationRepo.findOne({
    where: { userId, organizationId: WEME_ORGANIZATION_ID },
  });
  if (!association?.roles.includes(OrganizationRoles.Organization)) return;

  const roles = association.roles.filter(
    (r) => r !== OrganizationRoles.Organization,
  );
  await associationRepo.update(association.id, { roles });
}

export async function linkUserAsPartnerOrganizationAppManager(
  partnerOrganizationId: string,
  params: {
    name: string;
    phoneNumber: string;
    email?: string | null;
    partnerContactRole?: string | null;
  },
): Promise<string | null> {
  const phone = formatPhoneForPartnerManager(params.phoneNumber.trim());
  const name = params.name.trim();
  if (!name) return "יש להזין שם למנהל";
  if (!phone || phone.length < 12) return "מספר טלפון לא תקין";

  const email = params.email?.trim() ?? "";
  const partnerContactRole = params.partnerContactRole?.trim() || null;
  let userId: string;
  const existing = await userRepo.findFirst({ phoneNumber: phone });
  if (existing) {
    if (
      existing.partnerOrganizationId &&
      existing.partnerOrganizationId !== partnerOrganizationId
    ) {
      return "משתמש זה כבר מנהל של ארגון אחר";
    }
    if (existing.partnerOrganizationId === partnerOrganizationId) {
      await userRepo.update(existing.id, { email, partnerContactRole });
      await ensurePartnerManagerAssociation(existing.id);
      return null;
    }
    await userRepo.update(existing.id, {
      partnerOrganizationId,
      email,
      partnerContactRole,
    });
    userId = existing.id;
  } else {
    const created = await userRepo.insert({
      name,
      phoneNumber: phone,
      email,
      partnerContactRole,
      roles: [Roles.User],
      partnerOrganizationId,
      gender: "OTHER",
    });
    userId = created.id;
  }

  await ensurePartnerManagerAssociation(userId);
  return null;
}

export async function assignExistingUserAsPartnerOrganizationAppManager(
  userId: string,
  partnerOrganizationId: string | null,
): Promise<string | null> {
  const user = await userRepo.findId(userId);
  if (!user) return "משתמש לא נמצא";

  if (partnerOrganizationId) {
    if (
      user.partnerOrganizationId &&
      user.partnerOrganizationId !== partnerOrganizationId
    ) {
      return "משתמש זה כבר מנהל של ארגון אחר";
    }
    if (user.partnerOrganizationId !== partnerOrganizationId) {
      await userRepo.update(userId, { partnerOrganizationId });
    }
    await ensurePartnerManagerAssociation(userId);
    return null;
  }

  await userRepo.update(userId, { partnerOrganizationId: null });
  await removePartnerManagerAssociationRole(userId);
  return null;
}
